import { type FC } from 'react';
import { Link } from 'react-router-dom';
import { Box, Flex, useDisclosure } from '@chakra-ui/react';

import Heading from 'components/_core/Heading';
import SunshineCard from 'components/_core/SunshineCard';
import SquircleIcon from 'components/_core/SunshineSquircleIcon';
import SunshineTag from 'components/_core/SunshineTag';
import Text from 'components/_core/Text';
import ErrorView from 'components/ErrorView';
import Loader from 'components/Loader';
import { logEvent } from 'features/Analytics/analytics';
import TrackView from 'features/Analytics/TrackView';
import ReferralHowItWorksModal from 'features/Referral/components/Modals/ReferralHowItWorksModal/ReferralHowItWorksModal';
import ReferralCopyButton from 'features/Referral/components/ReferralCopyButton';
import ReferralEmailInviteButton from 'features/Referral/components/ReferralEmailInviteButton/indext';
import ReferralNoCodeCallout from 'features/Referral/components/ReferralNoCodeCallout/ReferralNoCodeCallout';
import ReferralOverview from 'features/Referral/components/ReferralOverview';
import { useReferral } from 'features/Referral/hooks/useReferral';
import * as routes from 'features/Referral/routes';
import {
  daysBeforeEndOfOffer,
  isDuringSpecialOffer,
  referrerRewardAmount,
} from 'features/Referral/utils';
import * as Layout from 'layouts/StepperLayout';

import locales from './locales';

const ReferralHome: FC = () => {
  const { error, loading, referralCodes, referrals } = useReferral();

  const detailsModal = useDisclosure();

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return <ErrorView />;
  }

  const promoCode = referralCodes[0]?.referralCode;

  return (
    <Layout.Root>
      <TrackView pageName="Referral Home" />
      <Layout.Body>
        <Heading marginBottom="space-16" size="lg">
          {locales.formatString(locales.title, {
            amount: isDuringSpecialOffer(new Date()) ? 250 : 200,
          })}
        </Heading>

        {isDuringSpecialOffer(new Date()) ? (
          <SunshineTag
            colorScheme="green"
            icon="gift"
            marginBottom="space-24"
            marginRight="auto"
            noOfLines={1}
            size="sm"
          >
            {daysBeforeEndOfOffer() > 7
              ? locales.badgeText
              : locales.formatString(locales.countDown, {
                  days: daysBeforeEndOfOffer(),
                })}
          </SunshineTag>
        ) : null}

        <Text marginBottom="space-24" size="md">
          {locales.formatString(locales.description, {
            amount: referrerRewardAmount(new Date()),
          })}
        </Text>

        <ReferralOverview referrals={referrals} />

        <SunshineCard.Group isBoxed={false} marginBottom="space-32">
          <SunshineCard
            alignItems="center"
            as={Link}
            onClick={() => {
              logEvent({
                name: 'Referral Your Referrals Clicked',
                usePrefix: false,
              });
            }}
            title={locales.yourReferrals}
            to={routes.list}
          >
            <SunshineCard.Slot name="aside">
              <SquircleIcon name="send" />
            </SunshineCard.Slot>
            <SunshineCard.Slot name="action">
              <SunshineCard.SunshineIcon name="chevron-right" />
            </SunshineCard.Slot>
          </SunshineCard>
          <SunshineCard
            alignItems="center"
            onClick={() => {
              logEvent({
                name: 'Referral HowItWorks Modal Opened',
                usePrefix: false,
              });
              detailsModal.onOpen();
            }}
            title={locales.howItWorks}
          >
            <SunshineCard.Slot name="aside">
              <SquircleIcon name="settings" />
            </SunshineCard.Slot>
          </SunshineCard>
        </SunshineCard.Group>

        {!promoCode ? <ReferralNoCodeCallout /> : null}

        {promoCode ? (
          <Flex alignItems="center" direction="row" gap={32}>
            <Box>
              <ReferralCopyButton promoCode={promoCode} />
            </Box>
            <Box>
              <ReferralEmailInviteButton promoCode={promoCode} />
            </Box>
          </Flex>
        ) : null}
      </Layout.Body>
      <ReferralHowItWorksModal {...detailsModal} />
    </Layout.Root>
  );
};

export default ReferralHome;
